import React, { useMemo } from 'react';
import type { Dispatch, Key, SetStateAction } from 'react';

import Tooltip from 'rc-tooltip';
import classNames from 'classnames';

import { Divider } from 'primereact/divider';
import { Button as PrimeButton } from 'primereact/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';

import { FlexBox } from '@eltoro-ui/components';

import { usePermissionContext } from 'contexts/PermissionContext';

import { SortingState } from '@tanstack/react-table';

import Table from 'Components/Table';
import type { PanelRender, TableRowSelection } from 'Components/Table/types';
import { ConfirmationModal, EmptyStatus } from 'Components';

import { getCampaignColumns } from 'Pages/Orderlines/helpers';

import { CampaignStatusEnum, CampaignStep } from 'enums';
import type { TCampaign, TOrderline, TResPagination } from 'types';

import './Orderlines.scss';

type CampaignsListProps = {
  campaigns: TCampaign[];
  fetchDataIsLoading: boolean;
  campaignPagination: TResPagination;
  onDelete: (_items: TCampaign[]) => Promise<void>;
  deleteCampaignData: TCampaign[];
  setDeleteCampaignData: Dispatch<SetStateAction<TCampaign[]>>;
  deleteModalIsOpen: boolean;
  setDeleteModalIsOpen: Dispatch<SetStateAction<boolean>>;
  deleteIsLoading: boolean;
  campaignPage: number;
  setCampaignPage: Dispatch<SetStateAction<number>>;
  campaignSorting: SortingState;
  setCampaignSorting: Dispatch<SetStateAction<SortingState>>;
  selectionRows: [Key[], Dispatch<SetStateAction<Key[]>>];
  selectionCampaigns: [TCampaign[], Dispatch<SetStateAction<TCampaign[]>>];
  changeRoutePath: (actionItem: TCampaign | TOrderline) => void;
  searchValue: string;
};
export default function CampaignsList({
  campaigns,
  fetchDataIsLoading,
  campaignPagination,
  onDelete,
  deleteCampaignData,
  setDeleteCampaignData,
  deleteModalIsOpen,
  setDeleteModalIsOpen,
  deleteIsLoading,
  campaignPage,
  setCampaignPage,
  changeRoutePath,
  searchValue,
  campaignSorting,
  setCampaignSorting,
  selectionRows: [selectedRowKeys, setSelectedRowKeys],
  selectionCampaigns: [selectedCampaigns, setSelectedCampaigns],
}: CampaignsListProps) {
  const { userIsActive } = usePermissionContext();

  const rowSelection = useMemo<TableRowSelection<TCampaign>>(
    () => ({
      selectedRowKeys,
      onChange: (selectedRowKeys, selectedRows) => {
        setSelectedRowKeys(selectedRowKeys);
        setSelectedCampaigns(prevCampaigns => {
          const filteredRows = prevCampaigns.filter(
            row => campaigns.findIndex(({ id }) => id === row.id) === -1
          );
          return [...filteredRows, ...selectedRows];
        });
      },
    }),
    [selectedRowKeys, campaigns]
  );

  const deleteBtnDisable = useMemo(
    () =>
      selectedCampaigns.every(
        campaign =>
          (campaign.status === CampaignStatusEnum.CREATING ||
            campaign.status === CampaignStatusEnum.IN_REVIEW) &&
          campaign.step === CampaignStep.DONE
      ),
    [selectedCampaigns]
  );

  const tableTitle: PanelRender<TCampaign> | undefined = selectedRowKeys.length
    ? () => {
        return (
          <FlexBox alignItems="center" gap="0.75rem">
            <span className="selected-items-count">
              {`${selectedRowKeys.length} contact${selectedRowKeys.length > 1 ? 's' : ''} selected`}
            </span>
            <Divider data-pr-classname="table-header-divider" layout="vertical" />
            <Tooltip
              placement="topLeft"
              showArrow
              classNames={{ root: classNames('audience-tooltip', 'save-eligible-contacts') }}
              align={{
                offset: [-20, -3],
                targetOffset: [-6, 0],
              }}
              overlay="Only draft campaigns can be deleted."
              {...(deleteBtnDisable ? {} : { visible: false })}
            >
              <PrimeButton
                text
                severity="danger"
                className="table-delete-btn"
                disabled={deleteBtnDisable}
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={() => setDeleteModalIsOpen(true)}
              />
            </Tooltip>
          </FlexBox>
        );
      }
    : undefined;

  return (
    <div className="Campaigns">
      <Table<TCampaign>
        rounded={false}
        columns={getCampaignColumns(userIsActive, changeRoutePath, (campaign: TCampaign) => {
          setDeleteModalIsOpen(true);
          setDeleteCampaignData([campaign]);
        })}
        data={campaigns}
        title={tableTitle}
        loading={fetchDataIsLoading}
        rowSelection={rowSelection}
        sortConfig={{
          sorting: campaignSorting,
          onSortingChange: setCampaignSorting,
        }}
        pagination={{
          hideOnSinglePage: true,
          pageSize: 50,
          current: campaignPage,
          total: campaignPagination.total_count,
          totalPages: campaignPagination.total_pages,
          onChange: page => setCampaignPage(page),
        }}
        emptyText={
          <EmptyStatus
            heading={
              searchValue && !campaigns.length
                ? 'No results found'
                : 'You don’t have any campaigns yet.'
            }
            subHeading={
              searchValue && !campaigns.length
                ? 'You might consider trying different keywords, double-checking for any spelling errors, or adjusting your filters.'
                : 'You can try by creating new campaign to get better audience.'
            }
          />
        }
      />
      {deleteModalIsOpen && (
        <ConfirmationModal
          heading="Are you sure you want to delete the selected campaign(s)?"
          note="Only draft campaigns can be deleted"
          onSubmit={() =>
            onDelete(deleteCampaignData.length ? deleteCampaignData : selectedCampaigns)
          }
          onCancel={() => setDeleteModalIsOpen(false)}
          loading={deleteIsLoading}
          okText="Yes, Delete"
          cancelText="No, Thanks"
          deletion
        />
      )}
    </div>
  );
}
